import React, { ReactNode } from "react";
import {
  noteByPianoKeyNumber,
  pianoKeyNumberByNote,
  NoteName,
} from "../../drills/shared/MusicLib";

import { FClef, GClef, GClef8vb, NoteHeadBlack, TimeSig4 } from "./StaffGlyphs";
import {
  barlineWidth,
  metadata,
  quarterNoteWidth,
  staffLineColor,
  staffLineWidth,
  staffSpace,
  stemWidth,
  symbolColor,
} from "./StaffMetadata";

/*
Behind Bars - The Definitive Guide to Music Notation (Elaine Gould)
Page numbers in comments refer to this book.
*/

function getNoteOffset(note: NoteName) {
  let count = 0;
  const pianoKey1 = pianoKeyNumberByNote(note);
  // top stave line (F5) is the zero offset
  const zeroOffsetPianoKey = pianoKeyNumberByNote("F5");
  const smallerKey =
    pianoKey1 <= zeroOffsetPianoKey ? pianoKey1 : zeroOffsetPianoKey;
  const biggerKey = smallerKey === pianoKey1 ? zeroOffsetPianoKey : pianoKey1;
  for (let i = smallerKey + 1; i <= biggerKey; i++) {
    const middleNote = noteByPianoKeyNumber(i);
    // Only notes CDEFGAB affect the staff offset
    if (middleNote.length === 2) {
      count++;
    }
  }
  return pianoKey1 < zeroOffsetPianoKey ? count : -count;
}

function Barline(props: { x: number; type?: "start" | "end" | "middle" }) {
  const height = 4 * staffSpace;
  const offset =
    props.type === "start"
      ? barlineWidth / 2
      : props.type === "end"
        ? -barlineWidth / 2
        : 0;
  return (
    <line
      strokeWidth={barlineWidth}
      stroke={staffLineColor}
      x1={props.x + offset}
      y1={-0.5 * staffLineWidth}
      x2={props.x + offset}
      y2={height + 0.5 * staffLineWidth}
    />
  );
}
Barline.defaultProps = {
  type: "middle",
};

const StaffLines = (props: { width: number; endBarlines: boolean }) => {
  const { width } = props;
  const staffLines = (
    <g strokeWidth={staffLineWidth}>
      <line x1="0" y1="0" x2={width} y2="0"></line>
      <line x1="0" y1={staffSpace} x2={width} y2={staffSpace}></line>
      <line x1="0" y1={2 * staffSpace} x2={width} y2={2 * staffSpace}></line>
      <line x1="0" y1={3 * staffSpace} x2={width} y2={3 * staffSpace}></line>
      <line x1="0" y1={4 * staffSpace} x2={width} y2={4 * staffSpace}></line>
    </g>
  );

  return (
    <>
      {props.endBarlines && (
        <>
          <Barline x={-0.5 * barlineWidth} type="start" />
          <Barline x={width + 0.5 * barlineWidth} type="end" />
        </>
      )}
      <g stroke={staffLineColor}>{staffLines}</g>
    </>
  );
};

const TimeSignature = () => (
  <g transform={`translate(0, ${staffSpace})`}>
    <TimeSig4 />
    <g transform={`translate(0, ${2 * staffSpace})`}>
      <TimeSig4 />
    </g>
  </g>
);

interface StemProps {
  direction: "up" | "down";
  heightInStaffSpace: number;
}

function Stem(props: StemProps) {
  // See https://w3c.github.io/smufl/gitbook/specification/glyph-registration-notes-flags.html
  const { stemUpSE, stemDownNW } = metadata.glyphsWithAnchors.noteheadBlack;
  // The y-coordinates are inverted in svg so we need to invert them
  const yStart =
    (props.direction === "up" ? stemUpSE.y : stemDownNW.y) * staffSpace * -1;
  const stemHeight =
    props.heightInStaffSpace * staffSpace +
    (props.direction === "up" ? yStart : -yStart);
  const yEnd = yStart + (props.direction === "up" ? -stemHeight : stemHeight);
  const x =
    props.direction === "up"
      ? stemUpSE.x * staffSpace - 0.5 * stemWidth
      : stemDownNW.x * staffSpace + 0.5 * stemWidth;
  return (
    <line
      x1={x}
      x2={x}
      y1={yStart}
      y2={yEnd}
      strokeWidth={stemWidth}
      stroke={symbolColor}
    />
  );
}

function LedgerLines(props: { semitoneOffset: number }) {
  const ledgerLines: ReactNode[] = [];
  if (props.semitoneOffset < -1) {
    for (
      let semitoneOffset = props.semitoneOffset;
      semitoneOffset <= -2;
      semitoneOffset++
    ) {
      if (semitoneOffset % 2 === -1) {
        continue;
      }
      const lineOffset = -(props.semitoneOffset - semitoneOffset) / 2;
      ledgerLines.push(<LedgerLine key={lineOffset} lineOffset={lineOffset} />);
    }
  } else if (props.semitoneOffset > 9) {
    for (
      let semitoneOffset = props.semitoneOffset;
      semitoneOffset >= 10;
      semitoneOffset--
    ) {
      if (semitoneOffset % 2 === 1) {
        continue;
      }
      const lineOffset = -(props.semitoneOffset - semitoneOffset) / 2;
      ledgerLines.push(<LedgerLine key={lineOffset} lineOffset={lineOffset} />);
    }
  }
  return <>{ledgerLines}</>;
}

/**
 *
 * @param props offset in ledger numbers (relative to note)
 */
function LedgerLine(props: { lineOffset: number }) {
  const xStart = 0.5 * quarterNoteWidth - staffSpace;
  // ledger lines are two spaces long; p. 26
  const length = 2 * staffSpace;
  const xEnd = xStart + length;
  const y = props.lineOffset * staffSpace;
  return (
    <line
      x1={xStart}
      x2={xEnd}
      y1={y}
      y2={y}
      strokeWidth={staffLineWidth}
      stroke={staffLineColor}
    />
  );
}

interface NoteProps {
  semitoneOffset: number;
  horizontalOffset: number;
}

function Note(props: NoteProps) {
  const stemDirection = props.semitoneOffset < 4 ? "down" : "up";
  // At least 3.5 * staff-size; must reach middle-staff
  const stemHeight = Math.max(3.5, Math.abs(props.semitoneOffset / 2 - 2));

  return (
    <>
      <g
        transform={`translate(${props.horizontalOffset}, ${
          (props.semitoneOffset / 2) * staffSpace
        })`}
      >
        <LedgerLines semitoneOffset={props.semitoneOffset} />
        <NoteHeadBlack />
        <Stem direction={stemDirection} heightInStaffSpace={stemHeight} />
      </g>
    </>
  );
}

function HorizontalOffset(props: { x: number; children: ReactNode }) {
  return <g transform={`translate(${props.x}, 0)`}>{props.children}</g>;
}

function NotesWithBarLines(props: { notes: StaffNote[] }) {
  const boxRadius = 0.5 * staffSpace;
  const elements: ReactNode[] = [];
  let horizontalOffset = staffSpace;
  for (let i = 0; i < props.notes.length; i++) {
    const note = props.notes[i];
    if (note.type === "activeQuestion" || note.type === "inactiveQuestion") {
      elements.push(
        <rect
          key={`rect-${i}-${note.type}`}
          x={horizontalOffset - 0.5 * quarterNoteWidth}
          y={-1.5 * staffSpace}
          width={2 * quarterNoteWidth}
          height={7 * staffSpace}
          rx={boxRadius}
          ry={boxRadius}
          fillOpacity="0"
          stroke={symbolColor}
          strokeOpacity={
            note.type === "activeQuestion" ? barlineWidth : 0.5 * barlineWidth
          }
          strokeWidth={
            note.type === "activeQuestion"
              ? 1.5 * barlineWidth
              : 0.5 * barlineWidth
          }
          strokeDasharray="10 6"
          pathLength="400"
        />
      );
    } else {
      const semitoneOffset = getNoteOffset(note.name);
      elements.push(
        <Note
          key={`note-${i}-${note.type}`}
          semitoneOffset={semitoneOffset}
          horizontalOffset={horizontalOffset}
        />
      );
    }
    if (note.effect === "highlight") {
      elements.push(
        <rect
          key={`effect-${i}-${note.effect}`}
          x={horizontalOffset - 0.5 * quarterNoteWidth}
          y={-1.5 * staffSpace}
          width={2 * quarterNoteWidth}
          height={7 * staffSpace}
          rx={boxRadius}
          ry={boxRadius}
          fillOpacity="0.4"
          strokeWidth={0}
        />
      );
    }
    if (i > 0 && i < props.notes.length - 1 && (i + 1) % 4 === 0) {
      horizontalOffset += quarterNoteWidth + staffSpace + barlineWidth / 2;
      elements.push(<Barline key={`barline-${i}`} x={horizontalOffset} />);
      horizontalOffset += staffSpace + barlineWidth / 2;
    } else {
      horizontalOffset += 3.5 * quarterNoteWidth;
    }
  }
  return <>{elements}</>;
}

export interface StaffNote {
  name: NoteName;
  duration?: "w" | "h" | "q" | "8th" | "16th";
  type?: "normal" | "inactiveQuestion" | "activeQuestion";
  effect?: "none" | "highlight";
}

export interface StaffProps {
  clef?: "none" | "gClef" | "gClef8vb" | "fClef";
  timeSignature?: "none" | "fourFour";
  notes?: StaffNote[];
  endBarlines?: boolean;
  verticalMargin?: number;
}

export default function Staff(props: StaffProps) {
  const horizontalMargin = staffSpace;
  const verticalMargin =
    typeof props.verticalMargin === "undefined"
      ? Staff.defaultProps.verticalMargin
      : props.verticalMargin;
  const height = 2 * verticalMargin + 4 * staffSpace;
  const gClefWidth = 671; // TODO gClef8vb = 664; fClef = 684
  const timeSignatureWidth = 470;

  const notes = props.notes || Staff.defaultProps.notes;

  const Clef =
    props.clef === "none"
      ? () => null
      : props.clef === "gClef8vb"
        ? GClef8vb
        : props.clef === "fClef"
          ? FClef
          : GClef;
  // Clef & time signature spacing: p. 41
  // Note spacing: p. 39
  const clefOffset = props.clef === "none" ? 0 : staffSpace;
  const timeSignatureOffset =
    clefOffset +
    (props.clef === "none" ? 0 : gClefWidth) +
    (props.timeSignature === "none" ? 0 : staffSpace);
  const notesOffset =
    timeSignatureOffset +
    (props.timeSignature === "none" ? 0 : timeSignatureWidth) +
    (props.clef === "none" ? 0 : staffSpace);

  const barCount = Math.floor(notes.length / 4);
  const singleBarContentWidth =
    2 * staffSpace + 3 * 3.5 * quarterNoteWidth + quarterNoteWidth;
  const barlinesWidth = barCount === 0 ? 0 : (barCount - 1) * barlineWidth;
  const barsTotalWidth = barCount * singleBarContentWidth + barlinesWidth;
  const leftoverNotes = notes.length % 4;
  const leftoverNotesWidth =
    leftoverNotes === 0
      ? 0
      : 2 * staffSpace +
        quarterNoteWidth +
        (leftoverNotes > 1 ? (leftoverNotes - 1) * 3.5 * quarterNoteWidth : 0);
  const staffWidth = notesOffset + barsTotalWidth + leftoverNotesWidth;
  const totalWidth = 2 * horizontalMargin + staffWidth;

  return (
    <svg viewBox={`0 0 ${totalWidth} ${height}`} style={{ maxHeight: "10rem" }}>
      <g transform={`translate(${horizontalMargin}, ${verticalMargin})`}>
        <StaffLines width={staffWidth} endBarlines={props.endBarlines!} />
        <HorizontalOffset x={clefOffset}>
          <Clef />
        </HorizontalOffset>
        <HorizontalOffset x={timeSignatureOffset}>
          {props.timeSignature !== "none" && <TimeSignature />}
        </HorizontalOffset>
        <HorizontalOffset x={notesOffset}>
          <NotesWithBarLines notes={notes} />
        </HorizontalOffset>
      </g>
    </svg>
  );
}

Staff.defaultProps = {
  clef: "gClef",
  timeSignature: "none",
  notes: [],
  endBarlines: false,
  verticalMargin: 4 * staffSpace,
} as Required<StaffProps>;
